import { useState, useEffect } from 'react';
import { hasPermission } from '../../../lib/permissions';
import apiClient from '../../../lib/api';

interface Service {
  serviceId: string;
  serviceName: string;
  price: number;
  quantity: number;
  name?: string; // Alternative field name
  id?: string;   // Alternative field name
}

interface Booking {
  id: string;
  booking_date: string;
  booking_time: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  notes: string;
  total_price: number;
  created_at: string;
  services: Service[];
  payment_method: string;
  payment_status: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  // Optional user info if booking was made by authenticated user
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
}

const BookingManager = () => {
  // If whole booking access is disabled, render an access denied message.
  // Respect both 'booking' and 'booking-page' keys for compatibility.
  // Temporarily disabled for testing
  // if (false && !hasPermission('booking') && !hasPermission('booking-page')) { ... } REMOVED CHECK

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [previousBookingCount, setPreviousBookingCount] = useState<number>(0);

  useEffect(() => {
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    loadBookings();

    // Auto-refresh more frequently to catch new bookings immediately
    const interval = setInterval(() => {
      loadBookings();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const bookingsData = await apiClient.getAllBookings();

      // Check for new bookings
      if (bookingsData.length > previousBookingCount && previousBookingCount > 0) {
        const newBookingsCount = bookingsData.length - previousBookingCount;
        // Show notification for new bookings
        if (newBookingsCount > 0) {
          const newBookings = bookingsData.slice(0, newBookingsCount);
          newBookings.forEach((booking: any) => {
            if (isSlotBasedBooking(booking.booking_time)) {
              // Show browser notification for new slot bookings
              if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('🎉 New Booking Received!', {
                  body: `${booking.customer_name} booked ${booking.booking_time} on ${new Date(booking.booking_date).toLocaleDateString()}`,
                  icon: '/favicon.ico'
                });
              }
            }
          });
        }
      }

      setBookings(bookingsData);
      setPreviousBookingCount(bookingsData.length);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load bookings:', error);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditBooking = (booking: Booking) => {
    // prevent editing if master admin disabled edits
    if (!hasPermission('booking-edit')) {
      alert('You do not have permission to edit bookings.');
      return;
    }
    setEditingBooking({ ...booking });
    setShowModal(true);
  };

  const handleSaveBooking = async () => {
    if (!editingBooking) return;

    // prevent save if edit permission missing (defense in depth)
    if (!hasPermission('booking-edit')) {
      alert('You do not have permission to save booking changes.');
      return;
    }

    try {
      await apiClient.updateBookingStatus(editingBooking.id, editingBooking.status);
      await loadBookings(); // Reload to get fresh data
      setShowModal(false);
      setEditingBooking(null);
    } catch (error) {
      console.error('Failed to update booking:', error);
      alert('Failed to update booking. Please try again.');
    }
  };

  const handleDeleteBooking = async (id: string) => {
    console.log('[BookingManager] handleDeleteBooking called with id:', id);
    if (!hasPermission('booking-delete')) {
      console.warn('[BookingManager] Permission denied for booking-delete');
      alert('You do not have permission to delete bookings.');
      return;
    }
    const ok = window.confirm('Are you sure you want to delete this booking? This action cannot be undone.');
    console.log('[BookingManager] confirm returned:', ok);
    if (!ok) return;

    try {
      console.log('[BookingManager] Proceeding with API delete for booking:', id);
      await apiClient.deleteBooking(id);
      console.log('[BookingManager] Delete successful, reloading data');
      await loadBookings(); // Reload to get fresh data
    } catch (error) {
      console.error('[BookingManager] Failed to delete booking:', error);
      alert('Failed to delete booking. Please try again.');
    }
  };

  const updateBookingStatus = async (id: string, status: Booking['status']) => {
    if (!hasPermission('booking-status')) {
      alert('You do not have permission to update booking status.');
      return;
    }
    try {
      await apiClient.updateBookingStatus(id, status);
      await loadBookings(); // Reload to get fresh data
    } catch (error) {
      console.error('Failed to update booking status:', error);
      alert('Failed to update booking status. Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return 'ri-time-line';
      case 'confirmed': return 'ri-check-line';
      case 'completed': return 'ri-check-double-line';
      case 'cancelled': return 'ri-close-line';
      default: return 'ri-question-line';
    }
  };

  const isSlotBasedBooking = (time: string) => {
    // Check if time is in 20-minute intervals (00, 20, 40)
    const [hours, minutes] = time.split(':').map(Number);
    return minutes % 20 === 0;
  };

  const filteredBookings = filterStatus === 'all'
    ? bookings
    : filterStatus === 'slots'
      ? bookings.filter(booking => isSlotBasedBooking(booking.booking_time))
      : filterStatus === 'old'
        ? bookings.filter(booking => !isSlotBasedBooking(booking.booking_time))
        : bookings.filter(booking => booking.status === filterStatus);

  const totalRevenue = bookings
    .filter(booking => booking.status === 'completed')
    .reduce((sum, booking) => sum + booking.total_price, 0);

  const bookingStats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
    slotBased: bookings.filter(b => isSlotBasedBooking(b.booking_time)).length,
    oldSystem: bookings.filter(b => !isSlotBasedBooking(b.booking_time)).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
        <span className="ml-2">Loading bookings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-error-warning-line text-red-500 text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold text-red-900 mb-2">Error Loading Bookings</h3>
        <p className="text-red-700 mb-4">{error}</p>
        <button
          onClick={loadBookings}
          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-all duration-300"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Booking Management</h3>
          <p className="text-sm text-gray-600 mt-1">
            <i className="ri-information-line mr-1"></i>
            New 20-minute slot system active - prevents double bookings
          </p>
          <p className="text-xs text-gray-500 mt-1">
            <i className="ri-time-line mr-1"></i>
            Last refreshed: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Revenue</p>
            <p className="text-xl font-bold text-primary-400">₹{totalRevenue.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Booking Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{bookingStats.total}</p>
            <p className="text-sm text-gray-600">Total</p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">{bookingStats.pending}</p>
            <p className="text-sm text-gray-600">Pending</p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{bookingStats.confirmed}</p>
            <p className="text-sm text-gray-600">Confirmed</p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{bookingStats.completed}</p>
            <p className="text-sm text-gray-600">Completed</p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">{bookingStats.cancelled}</p>
            <p className="text-sm text-gray-600">Cancelled</p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-600">{bookingStats.slotBased}</p>
            <p className="text-sm text-gray-600">Slot Bookings</p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-center">
            <p className="text-2xl font-bold text-orange-600">{bookingStats.oldSystem}</p>
            <p className="text-sm text-gray-600">Old System</p>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        <div className="flex space-x-2 overflow-x-auto">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer capitalize ${filterStatus === status
                ? 'bg-primary-400 text-black'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              {status === 'all' ? 'All Bookings' : status}
            </button>
          ))}
        </div>
        <div className="flex space-x-2 border-l pl-2">
          <button
            onClick={() => setFilterStatus('slots')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${filterStatus === 'slots'
              ? 'bg-purple-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            <i className="ri-time-line mr-1"></i>Slot Bookings
          </button>
          <button
            onClick={() => setFilterStatus('old')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${filterStatus === 'old'
              ? 'bg-orange-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            <i className="ri-history-line mr-1"></i>Old System
          </button>
        </div>
      </div>

      {/* Bookings List */}
      {!hasPermission('booking-view') ? (
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <p className="text-gray-600">You do not have permission to view bookings.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredBookings.map((booking, index) => {
            const isNewBooking = new Date(booking.created_at).getTime() > Date.now() - 60000; // Created in last minute
            return (
              <div key={booking.id} className={`bg-white rounded-2xl shadow-lg p-6 border-2 ${isNewBooking ? 'border-green-400 bg-green-50' : 'border-gray-200'} transition-all duration-500`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-primary-400 text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900">{booking.customer_name}</h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span><i className="ri-phone-line mr-1"></i>{booking.customer_phone}</span>
                        {booking.customer_email && <span><i className="ri-mail-line mr-1"></i>{booking.customer_email}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {isNewBooking && (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-500 text-white animate-pulse">
                        <i className="ri-sparkle-line mr-1"></i>NEW
                      </span>
                    )}
                    <span className={`px-3 py-1 text-sm rounded-full flex items-center ${getStatusColor(booking.status)}`}>
                      <i className={`${getStatusIcon(booking.status)} mr-1`}></i>
                      {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Services ({booking.services?.length || 0})</p>
                    <div className="space-y-1">
                      {booking.services?.map((service, idx) => (
                        <div key={idx} className="space-x-1">
                          <span className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                            {service.serviceName || service.name || 'Unknown Service'}
                          </span>
                          {service.quantity > 1 && (
                            <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              x{service.quantity}
                            </span>
                          )}
                          <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                            ₹{service.price}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Date & Time</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(booking.booking_date).toLocaleDateString()}
                    </p>
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-600">{booking.booking_time}</p>
                      {isSlotBasedBooking(booking.booking_time) ? (
                        <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                          <i className="ri-checkbox-circle-line mr-1"></i>Slot
                        </span>
                      ) : (
                        <span className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                          <i className="ri-alert-line mr-1"></i>Old
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Total Revenue</p>
                      <p className="text-xl font-bold text-primary-400">₹{totalRevenue.toLocaleString()}</p>
                    </div>
                    <button
                      onClick={() => {
                        loadBookings();
                        setLastRefresh(new Date());
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center"
                    >
                      <i className="ri-refresh-line mr-2"></i>
                      Refresh
                    </button>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Booked On</p>
                    <p className="text-sm text-gray-900">
                      {new Date(booking.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {booking.notes && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-1">Notes</p>
                    <p className="text-sm text-gray-800 bg-gray-50 p-2 rounded">{booking.notes}</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-2">
                    {booking.status === 'pending' && hasPermission('booking-status') && (
                      <>
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {booking.status === 'confirmed' && hasPermission('booking-status') && (
                      <button
                        onClick={() => updateBookingStatus(booking.id, 'completed')}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        Mark Complete
                      </button>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {hasPermission('booking-edit') ? (
                      <button
                        onClick={() => handleEditBooking(booking)}
                        className="text-blue-600 hover:text-blue-800 cursor-pointer"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400" title="Edit disabled">✕</span>
                    )}

                    {hasPermission('booking-delete') ? (
                      <button
                        onClick={() => handleDeleteBooking(booking.id)}
                        className="text-red-600 hover:text-red-800 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400" title="Delete disabled">✕</span>
                    )}

                    <a
                      href={`https://wa.me/${booking.customer_phone.replace(/[^0-9]/g, '')}?text=Hi ${booking.customer_name}, your booking for ${booking.booking_date} at ${booking.booking_time} has been ${booking.status}.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-800 cursor-pointer"
                    >
                      <i className="ri-whatsapp-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filteredBookings.length === 0 && hasPermission('booking-view') && (
        <div className="text-center py-12">
          <i className="ri-calendar-line text-gray-300 text-6xl mb-4"></i>
          <p className="text-gray-500 text-lg">
            {filterStatus === 'slots' ? 'No slot-based bookings found.' :
              filterStatus === 'old' ? 'No bookings from old system found.' :
                'No bookings found for selected filter.'}
          </p>
          {filterStatus === 'all' && bookings.length === 0 && (
            <p className="text-gray-400 text-sm mt-2">
              Bookings will appear here when customers make appointments using the new slot system.
            </p>
          )}
        </div>
      )}

      {/* Edit Booking Modal */}
      {showModal && editingBooking && hasPermission('booking-edit') && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Edit Booking</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Customer Name</label>
                  <input
                    type="text"
                    value={editingBooking.customer_name}
                    onChange={(e) => setEditingBooking({ ...editingBooking, customer_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Phone</label>
                  <input
                    type="tel"
                    value={editingBooking.customer_phone}
                    onChange={(e) => setEditingBooking({ ...editingBooking, customer_phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    readOnly
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Email</label>
                <input
                  type="email"
                  value={editingBooking.customer_email || ''}
                  onChange={(e) => setEditingBooking({ ...editingBooking, customer_email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Booking Date</label>
                  <input
                    type="date"
                    value={editingBooking.booking_date}
                    onChange={(e) => setEditingBooking({ ...editingBooking, booking_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    readOnly
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Booking Time</p>
                  <input
                    type="text"
                    value={editingBooking.booking_time}
                    onChange={(e) => setEditingBooking({ ...editingBooking, booking_time: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    placeholder="2:00 PM"
                    readOnly
                  />
                  {isSlotBasedBooking(editingBooking.booking_time) && (
                    <p className="text-xs text-green-600 mt-1">
                      <i className="ri-checkbox-circle-line mr-1"></i>
                      Valid 20-min slot (8:00 AM - 9:00 PM)
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Status</label>
                <select
                  value={editingBooking.status}
                  onChange={(e) => setEditingBooking({ ...editingBooking, status: e.target.value as Booking['status'] })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent pr-8"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Notes</label>
                <textarea
                  value={editingBooking.notes}
                  onChange={(e) => setEditingBooking({ ...editingBooking, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBooking}
                className="px-6 py-2 bg-primary-400 hover:bg-primary-500 text-black rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingManager;
